import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Eye } from 'lucide-react'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    createColumns,
    createDateFormatter,
} from '@/components/ui/data-table-customized'
import type {
    DataTableAction,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { formatFileSize } from '@/utils/function'
import { Attachment } from '@/modules/Upload/types'

interface IAttachmentProps {
    contractID: number
    attachments?: Attachment[]
}

const AttachmentComponent: React.FC<IAttachmentProps> = ({ attachments }) => {
    const { t } = useTranslation()
    const { t: tContract } = useTranslation('contract')
    const { openFilePreview, downloadFileWithPresigned } =
        useFilePreviewWithPresigned()

    const handlePreview = useCallback(
        async (attachment: Attachment) => {
            await openFilePreview(attachment)
        },
        [openFilePreview]
    )

    const columns: DataTableColumn<Attachment>[] = createColumns([
        {
            key: 'attachment_file_name',
            title: t('attachment_file_name'),
            width: 400,
            render: (value, row) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        handlePreview(row)
                    }}
                    className="text-primary hover:underline text-left truncate"
                >
                    {value as string}
                </button>
            ),
        },
        {
            key: 'attachment_file_size',
            title: t('attachment_file_size'),
            width: 120,
            render: (value) => formatFileSize(Number(value) || 0),
        },
        {
            key: 'created_at',
            title: tContract('created_at'),
            width: 160,
            cellClassName: 'text-right',
            formatter: createDateFormatter({ format: 'DD/MM/YYYY HH:mm' }),
        },
    ])

    const actions: DataTableAction<Attachment>[] = [
        {
            key: 'preview',
            label: tContract('contract-preview'),
            icon: <Eye className="w-4 h-4" />,
            onClick: handlePreview,
        },
        {
            key: 'download',
            label: tContract('download-file'),
            icon: <Download className="w-4 h-4" />,
            onClick: (row) => downloadFileWithPresigned(row),
        },
    ]

    return (
        <Paper>
            <DataTableCustomized<Attachment>
                columns={columns}
                rows={attachments || []}
                rowKey={(row) => String(row.attachment_id)}
                size="small"
                resizable
                actions={actions}
                emptyMessage={tContract('no-data')}
                toolbar={{
                    settings: {
                        show: true,
                        storageKey: 'contract-attachments',
                    },
                }}
            />
        </Paper>
    )
}

export default AttachmentComponent
